import { Injectable, inject, PLATFORM_ID, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { SafeStorage } from '@features/common';

@Injectable({ providedIn: 'root' })
export class DeviceService {
  #isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Device id used when `localStorage` cannot be read or written — Safari's
   * "Block All Cookies" (`SecurityError`) or an exhausted quota
   * (`QuotaExceededError`).
   *
   * Held here so the id stays stable for the lifetime of the page rather than
   * being regenerated on every call. `httpDeviceInterceptor` reads it on EVERY
   * browser request, so a fresh id per call would present each request as a
   * different device.
   */
  #inMemoryDeviceId: string | null = null;

  /**
   * A stable, non-identifying device id.
   *
   * Never throws. `httpDeviceInterceptor` calls this for every browser request,
   * including the bootstrap-blocking guest login, so an unguarded storage throw
   * here used to escape an app initializer and stop Angular hydrating — leaving
   * the server-rendered page on screen with every `(click)` handler dead
   * (BBS-0070). Persistence is now best-effort: when it is unavailable the id
   * lives in memory for the page lifetime instead.
   */
  deviceId = computed(() => {
    if (!this.#isBrowser) return 'server';

    const deviceIdKey = 'deviceId';
    const stored = SafeStorage.getItem(deviceIdKey);

    if (stored) return stored;

    // Reuse the in-memory id rather than minting a second one; on a browser
    // where the write below fails, this is the only thing keeping the id stable.
    this.#inMemoryDeviceId ??= btoa(`${DeviceService.uuid()}/${new Date().toISOString()}`);

    SafeStorage.setItem(deviceIdKey, this.#inMemoryDeviceId);

    return this.#inMemoryDeviceId;
  });

  /**
   * Generates an RFC4122 v4 UUID. `crypto.randomUUID()` is only defined in a
   * secure context (HTTPS / localhost); over plain HTTP iOS Safari leaves it
   * `undefined`, so we fall back to `crypto.getRandomValues` (available in
   * insecure contexts) rather than throwing and breaking every API request.
   */
  private static uuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }
}
